import { novaClient } from "@/lib/trpc";
import type { DrugScreenStatus } from "@/types";

// The generic user-update route accepts many user fields. This hook's name is a
// contract, and the narrowed type makes it enforceable. Nothing unrelated can
// ride through a mutation named "drug and background".
interface DrugAndBackgroundData {
  drugScreenStatus?: DrugScreenStatus;
  hasCriminalRecord?: boolean;
}

interface DrugAndBackgroundVariables {
  data: DrugAndBackgroundData;
  stafferId: number;
  userId: number;
}

// Caches are invalidated by userId rather than written directly: the sheet
// profile is keyed on (userId, jobId, city, state) and several entries can exist
// for one user, so there is no single input to setData against.
export function useUpdateSetDrugAndBackground() {
  const novaClientUtils = novaClient.useUtils();

  const mutation = novaClient.internal.user.active.update.useMutation({
    onSuccess: (_updatedUser, { userId }) => {
      void novaClientUtils.internal.user.active.getCandidacySheetData.invalidate(
        { userId },
      );
      // The edit lands in the trail, so the citation timeline under the row is
      // stale too.
      void novaClientUtils.internal.user.paperTrail.citationsForUser.invalidate(
        {
          userId,
        },
      );
    },
  });

  return {
    ...mutation,
    mutate: (
      variables: DrugAndBackgroundVariables,
      options?: Parameters<typeof mutation.mutate>[1],
    ) => {
      mutation.mutate(variables, options);
    },
    mutateAsync: (
      variables: DrugAndBackgroundVariables,
      options?: Parameters<typeof mutation.mutateAsync>[1],
    ) => mutation.mutateAsync(variables, options),
  };
}
